import type { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import type { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Material } from '@babylonjs/core/Materials/material';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import type { Scene } from '@babylonjs/core/scene';

import '@babylonjs/core/Rendering/outlineRenderer';

import { highlightParts } from '../outline';
import type { StationKind, TownPortal, ZonePayload } from '../../net/protocol';
import { renderFeatures, showHitboxes } from '../compat';
import { type Cell, cellTexture, FACE, GEAR, GROUND, SCENE } from '../models/atlas';
import { measureParts } from '../models/rig';
import {
  BIOME_OBSTACLES,
  PROPS,
  type PropSpec,
  type PlacedProp,
  type PropLibrary,
  stationProp,
  VENDOR_FOR,
} from '../models/props';
import {
  ARTISAN,
  ARTISAN_STAND,
  CHIMNEY,
  DOOR_FACE,
  emptyParts,
  LAMP_POST,
  MERLON,
  PLAIN_FACE,
  placeParts,
  SHOPFRONT,
  type Part,
  type SceneryParts,
} from '../models/scenery';
import { biomeStyle, PLAIN_EDGE, townPalette, type BiomeStyle } from './biomes';
import { paint, shadeColour, TEXTURE_SIZE, type Pattern } from './textures';
import {
  checker,
  exposedEdges,
  halfExtent,
  isWalkable,
  obstacleTiles,
  poolTiles,
  structureTiles,
  tileAt,
  tileCentre,
  walkableTiles,
} from './layout';
import {
  CAMERA_VISIBLE_FACINGS,
  cellHash,
  chimneys,
  FACING_STEP,
  houseFaces,
  lampPosts,
  merlons,
  rotateLocal,
  shopfronts,
} from './town';
import { registerGlow } from '../glow';
import { MIN_OUTLINE_WIDTH } from '../pixelate';
import { debrisFor } from './debris';
import { decalsFor, decalUv } from './decals';
import type { LightSpot, TorchLights } from './torchlight';
import { FixtureView } from './fixtureView';
import decalAtlasUrl from '../../assets/decals/decals.webp?url';
import { bakeVertexShade, buildingHeightScale, buildingTint, ROOF_SHADE, STRUCTURE_SHADE } from './shade';

// Wall height is per biome since 2026-09-29: `BiomeStyle.edge.height`, `PLAIN_EDGE` (2.6) by default.

/** Wall thickness. */
const WALL_THICKNESS = 0.7;

/** Height of the emissive trim capping each wall — the biome's signature light. */
const TRIM_HEIGHT = 0.18;
/** A dungeon pillar's footprint and how far it rises above its wall. Chosen by looking. */
const PILLAR_SIDE = 1.0;
const PILLAR_RISE = 0.35;
/** How far below the floor the liquid beyond a rim lies: deep enough to read as a drop. */
const BEYOND_DEPTH = 0.9;
/** How far the liquid runs past the zone's edge, into the fog. */
const BEYOND_MARGIN = 30;
/**
 * A `ground` beyond: how far below the floor, and how dark. Chosen by looking — deep enough that a
 * room's edge reads as a raised path, dark enough that the floor still reads as where to walk.
 */
const BEYOND_GROUND_DEPTH = 0.35;
const BEYOND_GROUND_SHADE = 0.45;

/** How deep a pit goes. Chosen: deep enough to read as a drop at the camera's angle. */
const PIT_DEPTH = 2.0;

/** How far above the floor a decal lies: enough not to z-fight it at the camera's distance. */
const DECAL_LIFT = 0.02;

/** The forest edge: trees per exposed wall edge, how far past the wall, and how tall. Chosen. */
const TREES_PER_EDGE = 3;
const TREE_OUT_MIN = 1.0;
const TREE_OUT_SPREAD = 2.2;
const TREE_FAR_HEIGHT = 4.2;
const TREE_FAR_SPREAD = 2.0;
const TREE_NEAR_HEIGHT = 2.0;

/** Diameter of the entry and exit sigils, in world units. */
const MARKER_DIAMETER = 3.2;

/**
 * The town portal's gate: a ring standing upright over a disc on the floor.
 *
 * Sized to be a **shape rather than a line**. One world unit is roughly 25 device
 * pixels at the gameplay camera and the pixelate post-process renders at
 * 1/`PIXEL_SIZE`, so anything below ~0.12 units is averaged away by the
 * downsample — `MIN_OUTLINE_WIDTH`'s floor. The ring is 0.34 thick, nearly three
 * times that, because a gate that vanishes at the only resolution the game is
 * played at is indistinguishable from a gate that was never drawn.
 */
const PORTAL_RING_DIAMETER = 2.6;
const PORTAL_RING_THICKNESS = 0.34;

/**
 * The gate's colour, and it deliberately belongs to no biome.
 *
 * Every other emissive surface in a zone is `style.trim` — the biome's signature
 * light — because walls and floors are *of* the place. A portal is not: it is a hole
 * to somewhere else, so it stays the same cold white-blue in the crypts and in the
 * caldera. A player learns one colour for "this takes me home".
 */
const PORTAL_COLOUR: [number, number, number] = [0.42, 0.70, 0.95];

/** How tall a house stands. Below the camera's sight line, so it never hides the player. */
const BUILDING_HEIGHT = 3.4;

/** A curtain wall is taller than a house — it is meant to read as enclosing the town. */
const RAMPART_HEIGHT = 4.6;

/** How far a house is inset from its tile edge, leaving a street. */
const BUILDING_INSET = 0.9;

/** Eaves, and the roof slab's thickness. */
const ROOF_OVERHANG = 0.7;
const ROOF_HEIGHT = 0.45;

/**
 * How tall a terrain obstacle stands, and how far it is inset from its tile.
 *
 * Shorter than a house and shorter than a wall (2.6): a boulder the height of the
 * walls would read as part of them and would hide the monsters behind it, which at
 * this camera is the difference between terrain and an occlusion bug. Taller than
 * the 1.8-unit character, though, or it reads as a rug.
 *
 * The inset is larger than a building's, so an obstacle never touches the floor's
 * tile edge — a block filling its cell exactly would look like a wall stub that
 * someone forgot to connect, and the gap is what says "you walk around this".
 */
const OBSTACLE_HEIGHT = 1.7;
/** A thin obstacle prop's mound: low enough to read as ground, high enough to read as an edge. Chosen. */
const MOUND_HEIGHT = 0.35;
const OBSTACLE_INSET = 1.6;

/**
 * A second, smaller block on top, so an obstacle is not a cube.
 *
 * The **sum** is what has to clear 1.8 and stay under the walls' 2.6, and the first
 * version of this got it wrong: 2.1 + 0.7 came to 2.8, a boulder standing taller
 * than the room it is in. A `NullEngine` test measuring the built bounding box caught
 * it — the numbers are not the shape, the same reason `bolts.test.ts` measures its
 * heads rather than its table.
 */
const OBSTACLE_CAP_HEIGHT = 0.45;
const OBSTACLE_CAP_INSET = 2.6;

/**
 * How far below the floor a pool's surface sits.
 *
 * The floor slabs are 0.4 tall centred at −0.2, so their top face is y = 0. A pool
 * at −0.35 is visibly *down* without being a pit: it reads as liquid in a basin
 * rather than as a hole with something at the bottom of it. It gets no wall on the
 * boundary — a wall belongs on a boundary with **void** — so this drop is the only
 * thing that separates water from floor, which is why it is not smaller.
 */
const POOL_DEPTH = 0.35;

/** The pool surface's own thickness. Thin: only the top face is ever seen. */
const POOL_THICKNESS = 0.12;

/**
 * How many times a wall surface repeats across one tile, for the town's box-UV houses — the one
 * surface still mapped per face. Everything in a combat zone is in world space (`worldUvs`): per
 * face, 8 units at one copy per 6 cut the pattern off at every tile edge.
 */
function wallRepeat(zone: ZonePayload, style: BiomeStyle): number {
  const units = style.wallUnits ?? style.surfaceUnits;
  if (!units) return 1;
  return Math.max(1, zone.tileSize / units);
}

/** World units per copy of the floor's surface, for `worldUvs`. A tile, when the biome says nothing. */
function floorUnits(zone: ZonePayload, style: BiomeStyle): number {
  return style.surfaceUnits ?? zone.tileSize;
}

/** World units per copy of the wall's surface, for `worldUvs`. */
function wallUnits(zone: ZonePayload, style: BiomeStyle): number {
  return style.wallUnits ?? style.surfaceUnits ?? zone.tileSize;
}

/**
 * Planar **world-space** UVs, one texture copy per `units` (2026-10-02): a face that looks up takes
 * its world X and Z, one that looks along X takes Z and height, one along Z takes X and height.
 *
 * **Why, since every box already had UVs.** A box's UVs run 0–1 across each face, so a surface
 * repeated `tileSize / units` times per tile — 8 / 6 = 1.33 for the ground sheet — and was cut off
 * mid-pattern at every tile boundary: a seam along every row and every column of the floor, reported
 * as *floor tiles don't seem to be perfectly repeatable in x and y*. The texture itself tiled; the
 * mapping did not. In world space the pattern runs on across tiles, walls and their corner extensions
 * at any scale, and the texture's own seamlessness is the only thing left to get right.
 */
export function worldUvs(mesh: Mesh, units: number): void {
  const pos = mesh.getVerticesData(VertexBuffer.PositionKind);
  const nrm = mesh.getVerticesData(VertexBuffer.NormalKind);
  if (!pos || !nrm || units <= 0) return;
  const uvs = new Float32Array((pos.length / 3) * 2);
  for (let i = 0, j = 0; i < pos.length; i += 3, j += 2) {
    const ax = Math.abs(nrm[i]!);
    const ay = Math.abs(nrm[i + 1]!);
    const az = Math.abs(nrm[i + 2]!);
    const x = pos[i]!;
    const y = pos[i + 1]!;
    const z = pos[i + 2]!;
    const [u, v] = ay >= ax && ay >= az ? [x, z] : ax >= az ? [z, y] : [x, y];
    uvs[j] = u / units;
    uvs[j + 1] = v / units;
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uvs);
}

/** Shades a colour triple, for roof and trim variants. */
function shadeTriple(
  base: [number, number, number],
  amount: number,
): [number, number, number] {
  return shadeColour(base, amount);
}

/** A colour scaled toward black, for an emissive lift derived from a diffuse. */
function scaleColour(
  base: readonly [number, number, number],
  factor: number,
): [number, number, number] {
  return [base[0] * factor, base[1] * factor, base[2] * factor];
}

/**
 * How far a world object's hover outline stands off it, in world units.
 *
 * **One output pixel**, and it tracks the entity highlight deliberately — a portal and a
 * shopkeeper are the same size of thing to a player, and a second number here would be a second
 * number to keep right. Lowered from 0.2 with `HIGHLIGHT_WIDTH` on the same report: Babylon draws
 * an outline by scaling the mesh, so a width chosen against the screen fills in any part thinner
 * than twice that width — and a lamp post, a merlon and a shopkeeper's arm all are.
 *
 * It cannot go lower: below `MIN_OUTLINE_WIDTH` the rim is averaged away by the pixelate pass and
 * the hover is provably wired and invisible, which is what the entity outline shipped as at 0.08.
 */
export const WORLD_HIGHLIGHT_WIDTH = MIN_OUTLINE_WIDTH;
const WORLD_HIGHLIGHT_COLOUR = new Color3(0.95, 0.8, 0.45);

/**
 * How far in front of a shopfront a click on its artisan walks you.
 *
 * Measured out from the station's **tile centre** along the way the stall faces, so the
 * player ends up at the counter rather than inside it: the assembly occupies the back
 * half of the tile, from `z = -3.9` to `z = -1.0` of an eight-unit cell, and this is two
 * units the other way. It stays well inside `Config.StationRange` (4.0) of the tile
 * centre, which is the point the server measures — the client walks to the counter, the
 * server checks the station.
 */
const STATION_APPROACH = 2.0;

/**
 * A thing in the world that can be hovered and clicked: a town portal, or an artisan.
 *
 * **A click on one of these is a movement order plus an intent, never a transition.**
 * Walking into a portal is what goes home, checked server-side at the end of `Step`
 * exactly as it always was; clicking it only starts the walk. That is what makes a
 * collider here safe where the comment on `syncPortals` used to say it was not: the gate
 * stands where the player would click to walk to it, and a click on the gate walks them
 * to the gate. It swallows the floor click and then does the same thing with it.
 */
export interface WorldObject {
  /** `portal:<id>` or `station:<KIND>`. Unique, and what a collider's metadata carries. */
  key: string;
  kind: 'portal' | 'station' | 'exit' | 'grind' | 'door' | 'breakable';
  /** The portal's id, the station's kind, `'EXIT'`, `'GRIND'`, or a fixture's id. */
  id: string;
  /** Where the *server* measures from — the gate, or the station's tile centre. */
  at: { x: number; z: number };
  /** Where a click walks the player. */
  approach: { x: number; z: number };
  /** The drawn geometry, outlined on hover. Never pickable. */
  parts: Mesh[];
  /**
   * What the hover outlines instead of `parts`, when the two differ (Phase 42). The collider is
   * measured from `parts`; a generated prop drawn over an invisible procedural part keeps that
   * collider, and the prop is what lights up.
   */
  outline?: Mesh[];
}

/** The box a table of scenery parts fills, in its own local frame. */
function partsBox(parts: readonly Part[]): { minX: number; maxX: number; minZ: number; maxZ: number; maxY: number } {
  const box = { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity, maxY: 0 };
  for (const part of parts) {
    box.minX = Math.min(box.minX, part.at[0] - part.size[0] / 2);
    box.maxX = Math.max(box.maxX, part.at[0] + part.size[0] / 2);
    box.minZ = Math.min(box.minZ, part.at[2] - part.size[2] / 2);
    box.maxZ = Math.max(box.maxZ, part.at[2] + part.size[2] / 2);
    box.maxY = Math.max(box.maxY, part.at[1] + part.size[1] / 2);
  }
  return box;
}

/**
 * **A prop stands where the geometry it replaces stood** (Phase 42, spec §4): a station centred on
 * the stall's footprint, a vendor at the shopkeeper's spot fitted to their height, a lamp fitted to
 * the post's. A station's *size* is its own (`PROPS[id].size`) — fitted to the stall's width, the
 * tall ones came out twice the height of a house.
 */
const STALL = partsBox(SHOPFRONT);
const STALL_CENTRE: [number, number, number] = [(STALL.minX + STALL.maxX) / 2, 0, (STALL.minZ + STALL.maxZ) / 2];
const ARTISAN_HEIGHT = partsBox(ARTISAN).maxY;
const LAMP_HEIGHT = partsBox(LAMP_POST).maxY;
/** The colour a street lamp lights the street with. Chosen: warm, a step paler than a torch. */
const TOWN_LAMP_LIGHT = [1.0, 0.78, 0.5] as const;
/**
 * Where the painted lantern hangs on the generated post, as fractions of its height: measured off
 * the placed prop's vertices (lantern centroid 0.70 up, 0.11 towards +Z), and shrunk to sit inside
 * the painted glass: at the procedural lantern's size its bloom hung beside the lantern as a block. The procedural lantern's glow
 * box is put there, because a generated texture does not emit and a lamp that stopped glowing would
 * be a post.
 */
const LAMP_LIGHT_AT = { y: 0.7, z: 0.11, scale: 0.4 };
/**
 * The portal arch's height, **chosen**: its opening is 0.36 of its height wide and 0.72 tall
 * (measured off the file), so a round 2.6-unit ring cannot sit inside it — at 3.9 the opening is a
 * doorway a character walks through, and the ring gives way to a glowing membrane in it.
 */
const PORTAL_ARCH_HEIGHT = 3.9;
const PORTAL_OPENING = { width: 0.34, height: 0.7 };
/** The obelisk beside the town's GRIND sigil: its height, and how far behind the sigil it stands. */
const OBELISK_HEIGHT = 3.4;

export interface ZoneLights {
  ambient: HemisphericLight;
  sun: DirectionalLight;
  shadows: ShadowGenerator;
  /**
   * The bloom pass, when the environment built one.
   *
   * **Opt-in, and that is the whole reason it is here rather than left to itself.** A glow
   * layer blooms every emissive material in the scene by default, and characters carry an
   * emissive lift purely so they read against a dark floor — bloom that and a player
   * becomes a lamp. Only the surfaces that are meant to *be* light are registered:
   * windows, wall trim and the portals.
   */
  glow?: GlowLayer | null;
  /** The point-light pool, when there is one: each build hands it the zone's `lightSpots`. */
  torches?: Pick<TorchLights, 'setSpots'> | null;
}

/**
 * Builds the zone's geometry and applies its biome to the scene.
 *
 * Everything is parented to one node and every mesh is merged per material, so
 * rebuilding a zone is one dispose and the whole floor is a couple of draw calls
 * rather than one per tile.
 */
export class ZoneRenderer {
  private root: TransformNode | null = null;
  private readonly materials: StandardMaterial[] = [];
  // `BaseTexture`, not `DynamicTexture`: the list now holds both the procedurally painted
  // canvases and the scene atlas's loaded cells, and both need disposing with the zone.
  private readonly textures: BaseTexture[] = [];
  /** The meshes the ground is made of, so entities can be picked against them. */
  private floors: Mesh[] = [];
  /** This zone's doors and breakables, as drawn. Rebuilt with the zone. */
  private fixtureView: FixtureView | null = null;
  /** This zone's light sources, for `ZoneLights.torches`. Rebuilt with the zone. */
  private lightSpots: LightSpot[] = [];
  private current: ZonePayload | null = null;

  /**
   * The town-portal gates, and the ids they were built for.
   *
   * Their own node and their own material rather than living under `root`, because
   * a portal appears and goes **during** a zone: everything else here is built once
   * from the seed and torn down with the layout, and folding a mid-zone object into
   * that would mean rebuilding the floor to show a gate.
   */
  private portalRoot: TransformNode | null = null;
  private portalMaterial: StandardMaterial | null = null;
  private portalIds = '';

  /**
   * Everything in the world that takes a click, keyed by `WorldObject.key`.
   *
   * Two lifetimes in one map, which is why the two clears are separate: an artisan
   * belongs to the town and goes with the seed-keyed rebuild, a portal appears and goes
   * *during* a zone and is torn down by `clearPortals`.
   */
  private readonly objects = new Map<string, WorldObject>();

  /** The generated props, when there are any (Phase 42). Null draws the procedural scene. */
  private props: PropLibrary | null = null;
  /** Placed props, their own node — see `PropLibrary`: never disposed through `root`. */
  private propRoot: TransformNode | null = null;
  private readonly placed: PlacedProp[] = [];
  private readonly portalProps: PlacedProp[] = [];

  constructor(
    private readonly scene: Scene,
    private readonly lights: ZoneLights,
  ) {}

  /** The floor meshes, for pointer picking. Empty until a zone is built. */
  get groundMeshes(): readonly Mesh[] {
    return this.floors;
  }

  /** The zone currently rendered, or null. */
  get zone(): ZonePayload | null {
    return this.current;
  }

  /**
   * The world object a picked mesh belongs to, or null.
   *
   * An O(1) metadata read, the same shape `EntityMeshes.idForMesh` has, because picks
   * land on the invisible collider and the collider is what carries the key.
   */
  objectForMesh(mesh: unknown): WorldObject | null {
    const key = (mesh as { metadata?: { worldObject?: unknown } } | null | undefined)?.metadata
      ?.worldObject;
    if (typeof key !== 'string') return null;
    return this.objects.get(key) ?? null;
  }

  /** The world object with a key, or null. For the HUD and for tests. */
  worldObject(key: string): WorldObject | null {
    return this.objects.get(key) ?? null;
  }

  /** Every clickable world object, in insertion order. */
  get worldObjects(): readonly WorldObject[] {
    return [...this.objects.values()];
  }

  /**
   * Outlines a world object, or clears it.
   *
   * An outline rather than a tint, for the reason entities use one: the five shopfronts
   * share the town's cloth and skin materials with each other, so tinting one artisan
   * would light up all five. `renderOutline` is per-mesh state, which is the granularity
   * this needs — and it is why an artisan's geometry is merged **per figure** rather than
   * into the town-wide groups the stalls and houses go into.
   */
  setObjectHighlight(key: string, on: boolean): void {
    const object = this.objects.get(key);
    if (!object) return;

    highlightParts(`object:${key}`, object.outline ?? object.parts, on, WORLD_HIGHLIGHT_WIDTH, WORLD_HIGHLIGHT_COLOUR);
  }

  /**
   * Replaces whatever is rendered with the given zone.
   *
   * Idempotent per seed: rebuilding the same zone is skipped, so a reconnect
   * that re-sends the ZONE frame does not churn the scene.
   */
  /**
   * Hands over the generated props. Takes effect on the next build — `refresh` forces one.
   */
  useProps(props: PropLibrary): void {
    this.props = props;
  }

  /**
   * Rebuilds the zone on screen, even though its seed has not changed — for when the props finish
   * loading after the zone was drawn. A no-op before the first zone.
   */
  refresh(): void {
    const zone = this.current;
    if (!zone) return;
    this.current = null;
    this.build(zone);
  }

  /** Places a prop under the zone's prop node, or returns null for the procedural fallback. */
  private placeProp(
    id: Parameters<PropLibrary['place']>[0],
    at: { x: number; z: number; yaw: number; y?: number },
    size: number | undefined,
    name: string,
  ): PlacedProp | null {
    if (!this.props || !this.propRoot) return null;
    const prop = this.props.place(id, this.propRoot, at, size, name);
    if (prop) this.placed.push(prop);
    return prop;
  }

  build(zone: ZonePayload): void {
    if (this.current && this.current.seed === zone.seed && this.current.biome === zone.biome) {
      return;
    }

    this.clear();
    this.current = zone;
    this.propRoot = new TransformNode('zone-props', this.scene);

    const style = biomeStyle(zone.biome);
    this.applyBiome(style);

    const root = new TransformNode('zone', this.scene);
    this.root = root;

    this.buildFloor(zone, style, root);
    this.buildWalls(zone, style, root);
    this.buildBeyond(zone, style, root);
    this.buildTreeLine(zone, style);
    this.buildDecals(zone, style, root);
    this.buildFixtures(zone, style, root);
    this.buildLights(zone, style, root);
    this.buildStructures(zone, style, root);
    this.buildObstacles(zone, style, root);
    this.buildPools(zone, style, root);
    this.buildTown(zone, style, root);
    this.buildMarkers(zone, style, root);
    this.lights.torches?.setSpots(this.lightSpots);
  }

  /**
   * Builds or removes the gates for the open town portals.
   *
   * Called every frame beside `build`, and idempotent in the same way: the ids are
   * compared as one string and nothing happens unless the set changed. Portals are
   * rare — at most one, usually none — so the common case is a string compare.
   *
   * **The gate is a click target now, and the reason the old comment here said it must
   * not be is the reason it can be.** A collider on a portal does sit between the cursor
   * and the floor and does swallow the walk order that reaches it — and a click on the
   * gate issues exactly that walk order, to exactly that spot. Nothing is lost, and the
   * player gets a hover that says the ring is a thing rather than scenery.
   *
   * What has **not** changed is that entering is a place. The click walks; walking in is
   * what goes home, checked server-side at the end of `Step`. There is still one
   * mechanism per outcome, and a modified client clicking a portal from across the map
   * gains a walk order it could have sent by clicking the floor.
   *
   * The drawn ring and disc stay unpickable, per the standing rule: one invisible box,
   * measured from what was built, takes every pick.
   */
  syncPortals(portals: readonly TownPortal[]): void {
    const ids = portals.map((p) => `${p.id}@${p.x},${p.z}`).join('|');
    if (ids === this.portalIds) return;
    this.portalIds = ids;

    this.clearPortals();
    if (portals.length === 0) return;

    const root = new TransformNode('zone-portals', this.scene);
    this.portalRoot = root;

    // One emissive material for every gate: they are all the same hole to the same
    // place, and a material per portal would be a draw call per portal for no
    // difference anyone can see.
    const mat = new StandardMaterial('zone-portal', this.scene);
    mat.diffuseColor = new Color3(...PORTAL_COLOUR);
    mat.emissiveColor = new Color3(...PORTAL_COLOUR);
    mat.specularColor = new Color3(0.02, 0.02, 0.02);
    if (!renderFeatures.specular) mat.disableLighting = true;
    mat.fogEnabled = renderFeatures.fog;
    this.portalMaterial = mat;

    for (const portal of portals) {
      // The ring is the gate — upright, so it reads as something to walk *through*
      // from an isometric camera, where a flat disc reads as a shadow. That is the
      // same finding that moved the entry and exit markers off the floor and into
      // the HUD.
      const ring = MeshBuilder.CreateTorus(
        `portal-ring-${portal.id}`,
        {
          diameter: PORTAL_RING_DIAMETER,
          thickness: PORTAL_RING_THICKNESS,
          tessellation: 20,
        },
        this.scene,
      );
      // Standing up: a torus is built in the XZ plane, so it needs a quarter turn.
      ring.rotation.x = Math.PI / 2;
      ring.position.set(portal.x, PORTAL_RING_DIAMETER / 2, portal.z);
      ring.material = mat;
      ring.isPickable = false;
      ring.parent = root;
      // The one colour that means *this takes me home*, and the one thing in a zone that
      // should read as a hole rather than a surface — so it blooms like the windows do.
      registerGlow(this.lights.glow, ring, 'gateway');

      // **Phase 42: a stone arch, and the glow moves into its opening.** The ring stays — hidden —
      // because the collider is measured from it and a click target must not change shape with an
      // asset. A glowing membrane in the doorway takes its material and its bloom.
      const arch =
        this.props && this.propRoot
          ? this.props.place('town_portal', root, { x: portal.x, z: portal.z, yaw: 0 }, PORTAL_ARCH_HEIGHT, `portal-arch-${portal.id}`)
          : null;
      const outline: Mesh[] = [];
      if (arch) {
        this.portalProps.push(arch);
        ring.isVisible = false;
        const membrane = MeshBuilder.CreatePlane(
          `portal-membrane-${portal.id}`,
          {
            width: PORTAL_ARCH_HEIGHT * PORTAL_OPENING.width,
            height: PORTAL_ARCH_HEIGHT * PORTAL_OPENING.height,
            sideOrientation: Mesh.DOUBLESIDE,
          },
          this.scene,
        );
        membrane.position.set(portal.x, (PORTAL_ARCH_HEIGHT * PORTAL_OPENING.height) / 2, portal.z);
        membrane.material = mat;
        membrane.isPickable = false;
        membrane.parent = root;
        registerGlow(this.lights.glow, membrane, 'gateway');
        outline.push(membrane, ...arch.meshes);
      }

      // **No disc under a gate.** Asked for directly: *the circular base for the waypoints is ok
      // but for the town portal I'd rather just have the ring and not the circular base under it.*
      //
      // It used to draw a 3.2-unit lit cylinder on the floor here, "so the entry range has
      // something under it". The entry range does not need marking: a gate is a ring you can see
      // from across the street and the server decides arrival within 2.5 units either way, so the
      // disc was telling the player something the ring already said while adding a second lit
      // surface that had to be kept *out* of the bloom to stop it swallowing the gate.
      //
      // The zone's entry and exit sigils keep theirs — those mark a *place* with nothing standing
      // at it, which is the case a disc is actually for.

      // One invisible box over the gate, measured from what was actually built rather than
      // written down here — the rule `measureRig` exists for. A hand-sized gate would
      // stop matching the ring the first time `PORTAL_RING_DIAMETER` moved.
      this.registerObject(
        {
          key: `portal:${portal.id}`,
          kind: 'portal',
          id: portal.id,
          at: { x: portal.x, z: portal.z },
          // A portal is entered by standing *in* it, so the walk goes to the gate itself
          // rather than to a spot in front of it: `PortalEntryRange` is 2.5 units and
          // the server is what decides the player has arrived.
          approach: { x: portal.x, z: portal.z },
          parts: [ring],
          outline: outline.length > 0 ? outline : undefined,
        },
        root,
      );
    }
  }

  dispose(): void {
    this.clear();
  }

  /**
   * Brings the doors and breakables to the snapshot's `down` and advances their animation. Called
   * every frame; cheap when nothing changed. A door that opened and a barrel that broke stop being
   * click targets here.
   */
  syncFixtures(down: ReadonlySet<number>, dt: number): void {
    for (const id of this.fixtureView?.sync(down, dt) ?? []) this.unregisterObject(`fixture:${id}`);
  }

  /** The drawn fixtures, for tests. */
  get fixtures(): FixtureView | null {
    return this.fixtureView;
  }

  /**
   * Doors and breakables (`zone/fixtureView.ts`, `design/LEVEL-FIXTURES.md`): drawn, and registered
   * as click targets — a door to walk through, a barrel to break.
   */
  private buildFixtures(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    if (!zone.fixtures || zone.fixtures.length === 0) return;
    const wood = this.material('zone-fixture-wood', [0.32, 0.22, 0.14], undefined, 0, GROUND.hubWall, 1, 0.85);
    const stone = this.material('zone-fixture-stone', style.wall, style.wallPattern, 43, style.wallCell, 1, style.wallTint ?? 1);
    this.fixtureView = new FixtureView(this.scene, zone, root, { wood, stone });
    for (const { fixture, parts } of this.fixtureView.visuals) {
      this.registerObject(
        {
          key: `fixture:${fixture.id}`,
          kind: fixture.kind === 'DOOR' ? 'door' : 'breakable',
          id: String(fixture.id),
          at: fixture.at,
          approach: fixture.at,
          parts,
        },
        root,
      );
    }
  }

  /** Forgets a click target and its collider. */
  private unregisterObject(key: string): void {
    if (!this.objects.delete(key)) return;
    this.scene.getMeshByName(`pick-${key}`)?.dispose(false, true);
  }

  // ── Scene-wide biome application ─────────────────────────────────────────

  private applyBiome(style: BiomeStyle): void {
    this.scene.clearColor = new Color4(...style.clear, 1);
    this.scene.fogColor = new Color3(...style.fog);
    this.scene.fogDensity = style.fogDensity;

    this.lights.ambient.diffuse = new Color3(...style.ambient);
    this.lights.ambient.groundColor = new Color3(
      style.ambient[0] * 0.4,
      style.ambient[1] * 0.4,
      style.ambient[2] * 0.4,
    );
    this.lights.ambient.intensity = style.ambientIntensity;

    this.lights.sun.diffuse = new Color3(...style.sun);
    this.lights.sun.intensity = style.sunIntensity;
  }

  // ── Geometry ─────────────────────────────────────────────────────────────

  private buildFloor(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    // Two shades in a checker so a large floor is not one flat plane. Each shade
    // is one merged mesh.
    const shades: [number, number, number][] = [style.floor, style.floorAlt];
    const buckets: Mesh[][] = [[], []];

    for (const tile of walkableTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);
      const slab = MeshBuilder.CreateBox(
        `floor-${tile.col}-${tile.row}`,
        { width: zone.tileSize, height: 0.4, depth: zone.tileSize },
        this.scene,
      );
      slab.position.set(centre.x, -0.2, centre.z);
      buckets[checker(tile.col, tile.row)]?.push(slab);
    }

    for (const [i, bucket] of buckets.entries()) {
      if (bucket.length === 0) continue;

      const merged = Mesh.MergeMeshes(bucket, true, true, undefined, false, false);
      if (!merged) continue;

      merged.name = `zone-floor-${i}`;
      merged.parent = root;
      worldUvs(merged, floorUnits(zone, style));
      merged.material = this.material(
        `zone-floor-${i}`,
        shades[i] ?? style.floor,
        style.floorPattern,
        // A different salt per shade, so the two checker halves are not the same
        // stones repeated — which reads as a tiling error rather than a floor.
        i * 31 + 1,
        style.floorCell,
        // World UVs (`worldUvs`): one copy per `surfaceUnits`, continuous across tiles.
        1,
        // The checker survives a painted floor as a slight shade between alternate tiles.
        (style.floorTint ?? 1) * (i === 0 ? 1 : 0.9),
      );
      merged.receiveShadows = true;
      // The ground is what click-to-move picks against.
      merged.isPickable = true;
      this.floors.push(merged);
    }
  }

  /**
   * What lies past the walls, where a biome's `edge.beyond` says (2026-09-29): its `liquid`, one
   * plane under the whole zone at `BEYOND_DEPTH`, lit by its own glow — lava past a caldera's rim,
   * black water past a marsh's bank, the void's mist past the abyss's. The floor slabs stand out of
   * it, so every room's edge is a ledge over it. Not pickable and not solid: the void is unwalkable
   * on the server whatever is drawn in it.
   */
  private buildBeyond(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const kind = style.edge?.beyond;
    if (!kind) return;
    const half = halfExtent(zone);
    const width = half.x * 2 + BEYOND_MARGIN * 2;
    const depth = half.z * 2 + BEYOND_MARGIN * 2;
    const plane = MeshBuilder.CreateGround('beyond', { width, height: depth }, this.scene);
    if (kind === 'ground') {
      plane.position.y = -BEYOND_GROUND_DEPTH;
      const floor = this.mergeGroup([plane], 'zone-beyond', style.floor, root, {
        cell: style.floorCell,
        pattern: style.floorPattern,
        salt: 7,
        // The floor's own surface at the floor's own scale, continuous with it.
        worldUnits: floorUnits(zone, style),
        tint: (style.floorTint ?? 1) * BEYOND_GROUND_SHADE,
      });
      if (floor) floor.receiveShadows = true;
      return;
    }
    plane.position.y = -BEYOND_DEPTH;
    // No bloom: a whole zone's worth of blooming surface washes the room out. The glow is the
    // emissive alone, a step under the pools', which do bloom.
    this.mergeInto([plane], 'zone-beyond', style.liquid, root, {
      emissive: [style.liquid[0] * style.liquidGlow * 0.8, style.liquid[1] * style.liquidGlow * 0.8, style.liquid[2] * style.liquidGlow * 0.8],
    });
  }

  /**
   * Flat things on a combat zone's floor — bones, ash, puddles, leaf litter — where a biome's
   * `decals` says (2026-10-01; placement and the why in `zone/decals.ts`). One quad per decal kind,
   * drawn as thin instances, so a biome's whole scatter is two draw calls on one texture.
   *
   * **Not pickable**: they lie over the floor, and click-to-move picks the floor. **Alpha-blended
   * a hair above it** (`DECAL_LIFT`), so they neither z-fight the floor nor stand off it. Skipped
   * with textures off, since a decal is nothing but its texture.
   */
  private buildDecals(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    if (!renderFeatures.textures) return;
    const placed = decalsFor(zone, style.decals);
    if (placed.length === 0) return;

    const atlas = new Texture(decalAtlasUrl, this.scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);
    atlas.hasAlpha = true;
    atlas.wrapU = atlas.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.textures.push(atlas);

    for (const spec of style.decals ?? []) {
      const mine = placed.filter((d) => d.id === spec.id);
      if (mine.length === 0) continue;

      const quad = MeshBuilder.CreateGround(`zone-decal-${spec.id}`, { width: 1, height: 1 }, this.scene);
      const uv = decalUv(spec.id);
      const uvs = quad.getVerticesData(VertexBuffer.UVKind);
      if (uvs) {
        for (let i = 0; i < uvs.length; i += 2) {
          uvs[i] = uv.u0 + uvs[i]! * (uv.u1 - uv.u0);
          uvs[i + 1] = uv.v0 + uvs[i + 1]! * (uv.v1 - uv.v0);
        }
        quad.setVerticesData(VertexBuffer.UVKind, uvs);
      }
      quad.parent = root;
      quad.isPickable = false;
      quad.receiveShadows = true;

      const mat = new StandardMaterial(`zone-decal-${spec.id}`, this.scene);
      mat.diffuseTexture = atlas;
      mat.useAlphaFromDiffuseTexture = true;
      mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
      mat.specularColor = new Color3(0.02, 0.02, 0.02);
      mat.fogEnabled = renderFeatures.fog;
      if (spec.glow) {
        const lit = atlas.clone();
        lit.level = spec.glow;
        mat.emissiveTexture = lit;
        this.textures.push(lit);
      }
      // The floor's own brightness, so a decal is not lit brighter than the ground it lies on.
      const tint = style.floorTint ?? 1;
      mat.diffuseColor = new Color3(tint, tint, tint);
      this.materials.push(mat);
      quad.material = mat;

      const matrices = new Float32Array(mine.length * 16);
      mine.forEach((d, i) => {
        Matrix.Scaling(d.size, 1, d.size)
          .multiply(Matrix.RotationY(d.yaw))
          .multiply(Matrix.Translation(d.x, DECAL_LIFT, d.z))
          .copyToArray(matrices, i * 16);
      });
      quad.thinInstanceSetBuffer('matrix', matrices, 16);
      quad.thinInstanceRefreshBoundingInfo(false);
    }
  }

  /**
   * Trees round a forest's rooms, where a biome's `edge.trees` says (2026-09-29): `deco_tree`, drawn
   * as thin instances in one call (`PropLibrary.placeMany`), three per exposed wall edge on the void
   * side, placed and sized by `cellHash` so a zone is the same forest every visit.
   *
   * **Tall only on the far side.** The camera looks from the north-east, so a tree beyond a room's
   * north or east wall stands between the camera and the room — there they are low, a hedge. Beyond
   * the south and west walls they stand full height behind the room, where they hide nothing.
   */
  private buildTreeLine(zone: ZonePayload, style: BiomeStyle): void {
    if (!style.edge?.trees || !this.props?.has('deco_tree') || !this.propRoot) return;
    const at: { x: number; z: number; yaw: number; size: number }[] = [];
    const half = zone.tileSize / 2;
    for (const tile of walkableTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);
      const edges = exposedEdges(zone, tile.col, tile.row);
      const sides: [boolean, number, number, boolean, boolean][] = [
        [edges.north, 0, -1, true, false],
        [edges.south, 0, 1, true, true],
        [edges.west, -1, 0, false, true],
        [edges.east, 1, 0, false, false],
      ];
      for (const [exposed, nx, nz, alongX, far] of sides) {
        if (!exposed) continue;
        for (let k = 0; k < TREES_PER_EDGE; k++) {
          const h1 = cellHash(tile.col * 7 + k * 13 + nx * 3, tile.row * 5 + k * 11 + nz * 3);
          const h2 = cellHash(tile.row * 17 + k, tile.col * 19 + k * 3);
          const t = ((k + 0.5) / TREES_PER_EDGE - 0.5) * zone.tileSize + (h1 - 0.5) * 1.6;
          const outward = half + TREE_OUT_MIN + h2 * TREE_OUT_SPREAD;
          at.push({
            x: centre.x + nx * outward + (alongX ? t : 0),
            z: centre.z + nz * outward + (alongX ? 0 : t),
            yaw: h1 * Math.PI * 2,
            size: far ? TREE_FAR_HEIGHT + h2 * TREE_FAR_SPREAD : TREE_NEAR_HEIGHT + h1 * 0.6,
          });
        }
      }
    }
    const trees = this.props.placeMany('deco_tree', this.propRoot, at, 'zone-treeline');
    if (trees) this.placed.push(trees);
  }

  /**
   * Light sources along a room's walls, where a biome's `lights` says: torches on the crypts',
   * caverns' and wastes' masonry (Phase 42.6, then every walled biome on 2026-10-01), a brazier,
   * lantern or rune stone standing on a low rim. Each is a `LightSpot` for `TorchLights`, which
   * lights the nearest four for real.
   *
   * **Only on the far walls** — the south and west edges of a room, whose inner face looks toward
   * the camera (it sits at +x, −z). A torch on a near wall would face away from the player and be
   * seen from behind through the top of the wall, if at all: the same reason a house face the camera
   * cannot see is not built (`CAMERA_VISIBLE_FACINGS`). A share of the eligible faces by hash, so a
   * zone is lit the same way every visit.
   *
   * **The flame gets a glow of its own**, blooming like a window: a generated texture does not emit,
   * and a torch that did not light anything would read as a stick on the wall. Chosen by looking:
   * the share, the heights and the colours.
   */
  private buildLights(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const spec = style.lights;
    if (!spec || !this.props?.has(spec.prop)) return;
    const edge = style.edge ?? PLAIN_EDGE;
    const half = zone.tileSize / 2;
    const flames: Mesh[] = [];
    for (const tile of walkableTiles(zone)) {
      const edges = exposedEdges(zone, tile.col, tile.row);
      const centre = tileCentre(zone, tile.col, tile.row);
      // On a wall, just off its face (the tile boundary, see `buildWalls`). On a rim, on its top.
      const off = spec.mount === 'wall' ? half - 0.04 : half + WALL_THICKNESS / 2;
      const y = spec.mount === 'wall' ? 1.0 : edge.height + TRIM_HEIGHT;
      // The far walls: south (+z) facing −z into the room, west (−x) facing +x.
      const faces: [boolean, number, number, number, number][] = [
        [edges.south, 0, off, Math.PI, 17],
        [edges.west, -off, 0, Math.PI / 2, 29],
      ];
      for (const [exposed, dx, dz, yaw, salt] of faces) {
        if (!exposed || cellHash(tile.col + salt, tile.row - salt) > spec.share) continue;
        const prop = this.placeProp(
          spec.prop,
          { x: centre.x + dx, z: centre.z + dz, y, yaw },
          spec.size,
          `zone-torch-${tile.col}-${tile.row}-${salt}`,
        );
        if (!prop) continue;
        let top = -Infinity;
        for (const m of prop.meshes) {
          m.computeWorldMatrix(true);
          top = Math.max(top, m.getBoundingInfo().boundingBox.maximumWorld.y);
        }
        // The flame: at the top of the prop, a little out along its facing, into the room.
        const at = {
          x: centre.x + dx + Math.sin(yaw) * spec.flameOut,
          y: top - spec.flameDrop,
          z: centre.z + dz + Math.cos(yaw) * spec.flameOut,
        };
        const flame = MeshBuilder.CreateBox('torch-flame', { width: 0.22, height: 0.3, depth: 0.22 }, this.scene);
        flame.position.set(at.x, at.y, at.z);
        flames.push(flame);
        // The light itself stands a little further into the room, so the wall behind it is lit
        // rather than the inside of the prop.
        this.lightSpots.push({ x: at.x + Math.sin(yaw) * 0.6, y: at.y, z: at.z + Math.cos(yaw) * 0.6, colour: spec.colour });
      }
    }
    this.mergeInto(flames, 'zone-torch-flames', [...spec.colour], root, { emissive: [...spec.colour], glow: true });
  }

  private buildWalls(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const walls: Mesh[] = [];
    const trims: Mesh[] = [];
    const half = zone.tileSize / 2;
    const edge = style.edge ?? PLAIN_EDGE;

    for (const tile of walkableTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);
      const edges = exposedEdges(zone, tile.col, tile.row);

      // **Each wall stands on the wall tile, its face exactly on the boundary** (2026-09-29). It
      // used to be centred on the boundary, half a thickness into the floor tile, while the server
      // stopped a body only at the boundary — so a character or a monster could stand half inside
      // a drawn wall: *we can actually go through the inner part of the wall and lose
      // enemies/character in the wall*. Now the face is where collision is, and `BodyClearance`
      // keeps a body's centre its half-width off it.
      const out = half + WALL_THICKNESS / 2;
      const open = (dc: number, dr: number) => isWalkable(tileAt(zone, tile.col + dc, tile.row + dr).kind);
      const sides: [boolean, number, number, boolean, number, number][] = [
        // exposed, offset, along X?, and whether each end meets a perpendicular wall of this tile
        [edges.north, 0, -out, true, open(-1, 0) ? 0 : 1, open(1, 0) ? 0 : 1],
        [edges.south, 0, out, true, open(-1, 0) ? 0 : 1, open(1, 0) ? 0 : 1],
        [edges.west, -out, 0, false, open(0, -1) ? 0 : 1, open(0, 1) ? 0 : 1],
        [edges.east, out, 0, false, open(0, -1) ? 0 : 1, open(0, 1) ? 0 : 1],
      ];

      for (const [exposed, ox, oz, alongX, extendLow, extendHigh] of sides) {
        if (!exposed) continue;

        // At an inner corner the two walls, both moved out onto their wall tiles, would leave the
        // corner square between them empty; the wall runs on by a thickness to fill it.
        const length = zone.tileSize + (extendLow + extendHigh) * WALL_THICKNESS;
        const shift = ((extendHigh - extendLow) * WALL_THICKNESS) / 2;
        const width = alongX ? length : WALL_THICKNESS;
        const depth = alongX ? WALL_THICKNESS : length;
        const dx = ox + (alongX ? shift : 0);
        const dz = oz + (alongX ? 0 : shift);

        const wall = MeshBuilder.CreateBox(
          'wall',
          { width, height: edge.height, depth },
          this.scene,
        );
        wall.position.set(centre.x + dx, edge.height / 2, centre.z + dz);
        walls.push(wall);

        // A glowing lip along the top edge. This is the only light most biomes
        // give the player to read the layout by.
        const trim = MeshBuilder.CreateBox(
          'trim',
          { width, height: TRIM_HEIGHT, depth },
          this.scene,
        );
        trim.position.set(centre.x + dx, edge.height + TRIM_HEIGHT / 2, centre.z + dz);

        // A pillar at the start of each wall run, standing proud of it into the room's side of the
        // wall tile — what makes a dungeon wall read as built rather than extruded.
        if (edge.pillars) {
          const along = alongX ? { x: -zone.tileSize / 2, z: 0 } : { x: 0, z: -zone.tileSize / 2 };
          const pillar = MeshBuilder.CreateBox(
            'pillar',
            { width: PILLAR_SIDE, height: edge.height + PILLAR_RISE, depth: PILLAR_SIDE },
            this.scene,
          );
          pillar.position.set(centre.x + ox + along.x, (edge.height + PILLAR_RISE) / 2, centre.z + oz + along.z);
          walls.push(pillar);
        }
        trims.push(trim);
      }
    }

    this.mergeInto(walls, 'zone-walls', style.wall, root, {
      shadows: true,
      pattern: style.wallPattern,
      salt: 17,
      cell: style.wallCell,
      worldUnits: wallUnits(zone, style),
      tint: style.wallTint,
    });
    // A hedge's cap is a faint line, not the crypt's lamp: `edge.trim` scales the signature light.
    const glow = edge.trim ?? 1;
    this.mergeInto(trims, 'zone-trim', style.wall, root, {
      emissive: [style.trim[0] * glow, style.trim[1] * glow, style.trim[2] * glow],
    });
  }

  /**
   * Buildings and ramparts: blocks standing *on* their tile.
   *
   * The difference from `buildWalls` is which side of the boundary the geometry
   * sits on. A wall is drawn along the edge between a floor tile and the void
   * beyond it — "the map stops here". A structure is drawn filling its own tile —
   * "something is standing here". A town made of the former is a town-shaped hole;
   * it needs the latter to look built.
   *
   * Buildings are inset slightly and given a roof course a shade darker, so a
   * terrace of them reads as separate houses rather than one long mass.
   */
  private buildStructures(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const blocks: Mesh[] = [];
    const roofs: Mesh[] = [];

    for (const tile of structureTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);
      const rampart = tile.kind === 'RAMPART';
      // **A house varies; a curtain wall does not.** A rampart is one continuous
      // fortification and a stepped one reads as broken masonry rather than as variety —
      // which is the difference between breaking up repetition and looking like a bug.
      const hash = cellHash(tile.col, tile.row);
      const height = rampart
        ? RAMPART_HEIGHT
        : BUILDING_HEIGHT * buildingHeightScale(hash);
      // A rampart is a continuous curtain, so it fills its tile edge to edge; a
      // house is inset, which is what leaves streets between a row of them.
      const inset = rampart ? 0 : BUILDING_INSET;
      const side = zone.tileSize - inset * 2;

      const block = MeshBuilder.CreateBox(
        `structure-${tile.col}-${tile.row}`,
        { width: side, height, depth: side },
        this.scene,
      );
      block.position.set(centre.x, height / 2, centre.z);
      // The contact shadow, baked per box **before** the merge — see `bakeVertexShade`.
      // After it, a vertex's height no longer says which building it belongs to.
      //
      // The tint rides the same buffer, and that is the only place it *can* ride: the whole
      // terrace merges into one mesh with one material, so a per-building colour has
      // nowhere else to live. A rampart is left alone with the rest of it.
      bakeVertexShade(block, STRUCTURE_SHADE, rampart ? 1 : buildingTint(hash));
      blocks.push(block);

      // A roof: a slightly wider, shallower slab on top. Cheap eaves, and enough
      // to break the silhouette of a block into something building-shaped.
      const roof = MeshBuilder.CreateBox(
        `roof-${tile.col}-${tile.row}`,
        { width: side + ROOF_OVERHANG, height: ROOF_HEIGHT, depth: side + ROOF_OVERHANG },
        this.scene,
      );
      roof.position.set(centre.x, height + ROOF_HEIGHT / 2, centre.z);
      // A roof is a shallow slab, so the ramp across it is nearly flat — what it gets is
      // the lift on its top face, which is the edge that catches the ambient and stops a
      // terrace reading as one extruded mass.
      // The roof takes its house's tint, or a terrace would come out with matching caps
      // over mismatched walls — which reads worse than no variation at all.
      bakeVertexShade(roof, ROOF_SHADE, rampart ? 1 : buildingTint(hash));
      roofs.push(roof);
    }

    // **Rubble at the foot of the walls**, in every zone rather than only in town.
    //
    // The town has shopfronts, doors, windows, beams, chimneys and lamps; `buildTown`
    // returns early outside the hub, so a combat zone had a floor, boxes and a trim line
    // and nothing else. This is the cheapest honest detail for a crypt — not windows,
    // which a crypt should not have, but a break in the line where a wall meets flat
    // ground. It merges into the structure mesh, so it costs one more span of the vertex
    // buffer and no draw call.
    for (const piece of debrisFor(zone)) {
      const stone = MeshBuilder.CreateBox(
        `debris-${piece.at.x.toFixed(2)}-${piece.at.z.toFixed(2)}`,
        { size: piece.size },
        this.scene,
      );
      stone.position.set(piece.at.x, piece.size / 2, piece.at.z);
      stone.rotation.y = piece.yaw;
      // Its own tint from its own position, so a bank of rubble is not one flat value —
      // and darker on average than the wall it fell from, because it is lying in the
      // shadow the wall casts.
      bakeVertexShade(stone, STRUCTURE_SHADE, 0.82);
      blocks.push(stone);
    }

    this.mergeInto(blocks, 'zone-structures', style.wall, root, {
      shadows: true,
      pattern: style.wallPattern,
      salt: 23,
      cell: style.wallCell,
      worldUnits: wallUnits(zone, style),
      tint: style.wallTint,
    });
    // Roofs are darker and untextured: a pattern at this scale reads as noise, and
    // the flat cap is what separates one house from the next from above.
    this.mergeInto(roofs, 'zone-roofs', shadeTriple(style.wall, -0.35), root, { shadows: true });
  }

  /**
   * Terrain obstacles: a block and a cap standing *on* their tile.
   *
   * The same side of the boundary a building's block sits on, and for the same
   * reason — an OBSTACLE is solid, so `exposedEdges` draws no wall against it and
   * this is the only geometry in the cell. A wall *and* a block in one place is the
   * glowing kerb that ran along the foot of every house in town.
   *
   * Separate from `buildStructures` rather than a case inside it, because a boulder
   * is not a house: no roof course, no eaves, a deeper inset and a shorter, tapered
   * silhouette. Folding the two together would have put a roof on every rock the
   * moment OBSTACLE joined `isSolid`.
   *
   * It uses the wall's pattern lightened a little, so terrain reads as the same
   * rock the zone is cut out of rather than as an imported prop.
   */
  private buildObstacles(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const blocks: Mesh[] = [];
    // Floor under a dressed obstacle. An obstacle tile has none — the block covered the hole — and
    // a prop smaller than the block stood in a black pit. **Not added to `floors`**: those are what
    // click-to-move picks against, and a solid tile must not become a place to walk to.
    const plinths: Mesh[] = [];

    for (const tile of obstacleTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);

      // **The crypts' terrain, painted** (Phase 42.6): a fallen column, a sarcophagus, a bone pile
      // or an altar, one per tile by hash and turned a quarter-step by it too, so a room of three
      // is not three of the same thing facing the same way. On an OBSTACLE tile only — solid on the
      // server already, which is what wall rubble never was and why it was switched off.
      // Every biome with a set does the same since 2026-09-29 (`BIOME_OBSTACLES`).
      const set = BIOME_OBSTACLES[style.id];
      if (set && set.length > 0) {
        const pick = cellHash(tile.col, tile.row);
        const id = set[Math.floor(pick * set.length) % set.length]!;
        const turn = Math.floor(cellHash(tile.row + 101, tile.col + 7) * 4) * (Math.PI / 2);
        const onMound = (PROPS[id] as PropSpec).mound === true;
        const dressed = this.placeProp(id, { x: centre.x, z: centre.z, yaw: turn, y: onMound ? MOUND_HEIGHT : 0 }, undefined, `obstacle-${tile.col}-${tile.row}-${id}`);
        if (dressed) {
          const slab = MeshBuilder.CreateBox(
            `obstacle-floor-${tile.col}-${tile.row}`,
            { width: zone.tileSize, height: 0.4, depth: zone.tileSize },
            this.scene,
          );
          slab.position.set(centre.x, -0.2, centre.z);
          plinths.push(slab);
          // A thin prop stands on a low mound the size of the solid tile, so the edge a body stops
          // at is drawn (see `PropSpec.mound`).
          if (onMound) {
            const mound = MeshBuilder.CreateBox(
              `obstacle-mound-${tile.col}-${tile.row}`,
              { width: zone.tileSize - OBSTACLE_INSET * 2, height: MOUND_HEIGHT, depth: zone.tileSize - OBSTACLE_INSET * 2 },
              this.scene,
            );
            mound.position.set(centre.x, MOUND_HEIGHT / 2, centre.z);
            blocks.push(mound);
          }
          continue;
        }
      }

      const side = zone.tileSize - OBSTACLE_INSET * 2;
      const capSide = zone.tileSize - OBSTACLE_CAP_INSET * 2;

      const block = MeshBuilder.CreateBox(
        `obstacle-${tile.col}-${tile.row}`,
        { width: side, height: OBSTACLE_HEIGHT, depth: side },
        this.scene,
      );
      block.position.set(centre.x, OBSTACLE_HEIGHT / 2, centre.z);
      blocks.push(block);

      // A narrower cap, so the silhouette steps in rather than ending square. Two
      // boxes is all a 30 × 45 device-pixel target needs to stop reading as a cube.
      const cap = MeshBuilder.CreateBox(
        `obstacle-cap-${tile.col}-${tile.row}`,
        { width: capSide, height: OBSTACLE_CAP_HEIGHT, depth: capSide },
        this.scene,
      );
      cap.position.set(centre.x, OBSTACLE_HEIGHT + OBSTACLE_CAP_HEIGHT / 2, centre.z);
      blocks.push(cap);
    }

    this.mergeInto(plinths, 'zone-obstacle-floor', style.floor, root, {
      shadows: true,
      pattern: style.floorPattern,
      salt: 1,
      cell: style.floorCell,
      worldUnits: floorUnits(zone, style),
      tint: style.floorTint,
    });

    // The obstacles share the wall's cell rather than getting one of their own: a boulder and
    // the room it stands in are the same rock, and the tint above is what separates them.
    this.mergeInto(blocks, 'zone-obstacles', shadeTriple(style.wall, 0.12), root, {
      shadows: true,
      pattern: style.wallPattern,
      salt: 29,
      cell: style.wallCell,
      worldUnits: wallUnits(zone, style),
      tint: style.wallTint,
    });
  }

  /**
   * Pools: a recessed surface, and no wall around it.
   *
   * A pool is unwalkable and **not** solid, which is the third combination the two
   * axes already allowed and the reason no third axis was added for this phase. What
   * it means for the renderer is that neither of the other two builders touches it:
   * `walkableTiles` excludes it so it gets no floor slab, `exposedEdges` only walls
   * against void so its neighbours stay open, and `structureTiles` excludes it so
   * nothing is drawn standing in it. Without this method a pool would be a hole with
   * the backdrop visible through it.
   *
   * Emissive rather than lit, scaled by the biome's `liquidGlow`: these are the
   * darkest scenes in the game, and a lava pool that only reflected the ambient
   * light would be a black square in a room lit at 0.34.
   */
  private buildPools(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    if (style.pool === 'pit') {
      this.buildPits(zone, style, root);
      return;
    }
    const surfaces: Mesh[] = [];

    for (const tile of poolTiles(zone)) {
      const centre = tileCentre(zone, tile.col, tile.row);
      const surface = MeshBuilder.CreateBox(
        `pool-${tile.col}-${tile.row}`,
        { width: zone.tileSize, height: POOL_THICKNESS, depth: zone.tileSize },
        this.scene,
      );
      // Sunk below the floor's top face, which is what reads as a basin.
      surface.position.set(centre.x, -POOL_DEPTH - POOL_THICKNESS / 2, centre.z);
      surfaces.push(surface);
    }

    this.mergeInto(surfaces, 'zone-pools', style.liquid, root, {
      // Lava and bog-light are the zone's own light, pooled on the floor — the one
      // environment surface that should bloom. The trim above deliberately does not.
      glow: true,
      emissive: [
        style.liquid[0] * style.liquidGlow,
        style.liquid[1] * style.liquidGlow,
        style.liquid[2] * style.liquidGlow,
      ],
    });
  }

  /**
   * A pit where a pool would be (`BiomeStyle.pool`): the tile's four sides lined in the biome's wall
   * down to `PIT_DEPTH`, and a near-black floor at the bottom. Nothing glows — a pit is the absence of
   * light, not a source of it.
   */
  private buildPits(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const sides: Mesh[] = [];
    const bottoms: Mesh[] = [];
    const half = zone.tileSize / 2;
    for (const tile of poolTiles(zone)) {
      const c = tileCentre(zone, tile.col, tile.row);
      for (const [dx, dz, alongX] of [[0, -1, true], [0, 1, true], [-1, 0, false], [1, 0, false]] as const) {
        const side = MeshBuilder.CreateBox(
          'pit-side',
          { width: alongX ? zone.tileSize : 0.2, height: PIT_DEPTH, depth: alongX ? 0.2 : zone.tileSize },
          this.scene,
        );
        side.position.set(c.x + dx * (half - 0.1), -PIT_DEPTH / 2, c.z + dz * (half - 0.1));
        sides.push(side);
      }
      const bottom = MeshBuilder.CreateBox('pit-bottom', { width: zone.tileSize, height: 0.1, depth: zone.tileSize }, this.scene);
      bottom.position.set(c.x, -PIT_DEPTH, c.z);
      bottoms.push(bottom);
    }
    this.mergeInto(sides, 'zone-pit-sides', style.wall, root, {
      pattern: style.wallPattern,
      salt: 47,
      cell: style.wallCell,
      worldUnits: wallUnits(zone, style),
      tint: (style.wallTint ?? 1) * 0.6,
    });
    this.mergeInto(bottoms, 'zone-pit-bottoms', [0.02, 0.02, 0.025], root);
  }

  /**
   * The town's detail: shopfronts with artisans at them, houses with doors and lit
   * windows, street lamps and battlements.
   *
   * **Only in the hub**, keyed off `zone.kind` rather than off the presence of
   * `BUILDING` tiles: a ruin standing in a crypt is not a shop, and a door on it
   * would be an invitation into a block that is not hollow.
   *
   * Everything is built from the layout the server already sent — the tile grid and
   * the `stations` list — and **the artisans are still not entities**. There is no
   * shopkeeper in the snapshot, nothing simulated and nothing to kill; what they gained
   * is an *identity*, and it was already on the wire. A station's `kind` is the id a
   * click needs, so promoting the figures to click targets cost a collider each and no
   * protocol change at all. `HUB-ZONES-SCRAP-UI.md` records what is left for Phase
   * 13's vendors.
   *
   * The price of the promotion is where the artisans are merged. A stall's geometry
   * still goes into the town-wide groups; a *figure* is merged per shop, because
   * `renderOutline` is per-mesh and an artisan folded into `town-cloth` would light up
   * all five of them on hover. Five figures at three materials each, against the one
   * merge they used to contribute to.
   *
   * It rides the seed-keyed rebuild, unlike `syncPortals`, and that is correct: a
   * town does not gain a house halfway through a visit. The hub is fixed rather than
   * seeded, so this runs once on arrival and once more only if the player leaves and
   * comes back.
   *
   * Six materials plus one per shop, and every group merged once — twenty houses,
   * five stalls, five figures, six lamps and a hundred and twenty-eight merlons come
   * to eleven draw calls.
   */
  private buildTown(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    if (zone.kind !== 'HUB') return;

    const palette = townPalette(style);
    const shared = emptyParts();
    // Accent geometry is grouped by shop rather than pooled, because the accent *is*
    // which shop it is. Five materials, and a stall's awning, sign, stock and the
    // shopkeeper's apron all land in the same one.
    const perShop = new Map<StationKind, SceneryParts>();

    const shopParts = (kind: StationKind): SceneryParts => {
      const existing = perShop.get(kind);
      if (existing) return existing;
      // Shares every neutral group with the town and keeps only `accent` private, so
      // a stall's timber merges with the doors and its glow with the windows.
      const parts: SceneryParts = { ...shared, accent: [] };
      perShop.set(kind, parts);
      return parts;
    };

    for (const front of shopfronts(zone)) {
      const parts = shopParts(front.kind);
      const placement = { origin: { x: front.at.x, y: 0, z: front.at.z }, yaw: front.yaw };
      // **The generated station, where there is one** (Phase 42), in the back half of the tile
      // where the stall stood and opening the same way. Its accent — the shop's colour — goes with
      // the stall: a forge reads as a forge without a coloured banner saying so.
      const [cx, , cz] = rotateLocal(STALL_CENTRE, front.yaw);
      const station = this.placeProp(
        stationProp(front.kind),
        { x: front.at.x + cx, z: front.at.z + cz, yaw: front.yaw },
        undefined,
        `town-station-${front.kind}`,
      );
      if (!station) placeParts(this.scene, SHOPFRONT, placement, parts, `town-stall-${front.kind}`);

      // The shopkeeper stands at the end of their own counter, turned to face the
      // street with it — one local offset through the same yaw as the stall.
      //
      // Into a group of their own, not the town's: they are a click target, and a hover
      // outline is per-mesh.
      const figure = emptyParts();
      const [sx, , sz] = rotateLocal(ARTISAN_STAND, front.yaw);
      // The painted vendor, standing in its bind pose, fitted to the box shopkeeper's height —
      // and the hover's parts, so the collider is measured from the figure that is drawn.
      const vendor = this.placeProp(
        VENDOR_FOR[front.kind],
        { x: front.at.x + sx, z: front.at.z + sz, yaw: front.yaw },
        ARTISAN_HEIGHT,
        `town-vendor-${front.kind}`,
      );
      const drawn: Mesh[] = vendor ? [...vendor.meshes] : [];
      if (!vendor) placeParts(
        this.scene,
        ARTISAN,
        { origin: { x: front.at.x + sx, y: 0, z: front.at.z + sz }, yaw: front.yaw },
        figure,
        `town-artisan-${front.kind}`,
      );

      // **An artisan is merged per figure, not into the shared groups**, so a hover can outline one
      // shopkeeper without lighting all five — and that is why the cells have to be named again
      // here. Adding them to `town-cloth`/`town-skin` above textured the stalls' shared geometry
      // and left every shopkeeper a flat cube, which is what the first pass of this shipped as.
      //
      // `accent` stays flat: it is the shop's identifying colour and matches its icon in the
      // workshop row, so a painted surface would spend the one patch whose job is being a colour.
      const artisanGroups: [keyof SceneryParts, [number, number, number], Cell | null][] = [
        ['cloth', palette.cloth, GEAR.burlap],
        // **Skin is a colour; the face is a painting on a plate of its own.** They shared this
        // group until 2026-08-13, and since a box wears one material on all six sides that put a
        // portrait on the back of every shopkeeper's head — and on both hands, which are also
        // skin. The comment here used to defend it as "the trade the monster heads already make,
        // and at ~10 output pixels the front is what reads": the pixel figure came from assuming
        // the pixelate pass downsamples by 3, and that pass is off by default.
        ['skin', palette.skin, null],
        ['face', palette.skin, FACE.skinWeathered],
        ['accent', palette.accents[front.kind], null],
      ];
      for (const [group, colour, cell] of vendor ? [] : artisanGroups) {
        const flesh = group === 'skin' || group === 'face';
        const merged = this.mergeGroup(figure[group], `town-artisan-${front.kind}-${group}`, colour, root, {
          shadows: !flesh,
          emissive: scaleColour(colour, flesh ? 0.42 : 0.45),
          cell: cell ?? undefined,
        });
        if (merged) drawn.push(merged);
      }

      // Two units into the street, along the way the stall faces: the assembly fills the
      // back half of its tile, so this is the counter rather than inside it — and it is
      // well within `Config.StationRange` of the tile centre, which is the point the
      // server measures against when the panel asks to open.
      const [fx, , fz] = rotateLocal([0, 0, STATION_APPROACH], front.yaw);
      this.registerObject(
        {
          key: `station:${front.kind}`,
          kind: 'station',
          id: front.kind,
          at: { x: front.at.x, z: front.at.z },
          approach: { x: front.at.x + fx, z: front.at.z + fz },
          parts: drawn,
        },
        root,
      );
    }

    // Doors, windows and beams straddle the block's own face. A building already
    // draws its block, so nothing here is coplanar with it — that is the glowing-kerb
    // rule applied to a door instead of a wall.
    const faceOffset = zone.tileSize / 2 - BUILDING_INSET;
    for (const face of houseFaces(zone)) {
      // **A face the camera cannot see is not built at all**, and the reason is its windows.
      //
      // Reported: *window glow is passing directly through the house walls, which should not be —
      // houses are not transparent.* They are not, and nothing was drawn through them: `GlowLayer`
      // composites its blurred glow map over the finished frame **additively and without any depth
      // test**, so a lit window anywhere in the scene brightens whatever pixels it covers,
      // including the roof and wall of the house it is set into. There is no depth-aware mode to
      // switch on — the pass has thrown away the depth buffer by then.
      //
      // So the fix is upstream of the glow: do not light a window nobody can see. The camera is
      // fixed at alpha −π/4, beta π/4, which shows a box only its **north and east** faces — that
      // is already why `CAMERA_FACING_ORDER` exists and why the door goes on the first open facing
      // rather than a fixed one. A south or west face was invisible geometry whose only effect on
      // the screen was its bloom leaking over the building in front of it.
      //
      // Dropping them also removes four merged parts per enclosed house, so the town gets slightly
      // cheaper for the same picture.
      if (!CAMERA_VISIBLE_FACINGS.includes(face.facing)) continue;

      const [dc, dr] = FACING_STEP[face.facing];
      placeParts(
        this.scene,
        face.door ? DOOR_FACE : PLAIN_FACE,
        {
          origin: {
            x: face.at.x + dc * faceOffset,
            y: 0,
            z: face.at.z + dr * faceOffset,
          },
          yaw: face.yaw,
        },
        shared,
        `town-house-${face.col}-${face.row}-${face.facing}`,
      );
    }

    const roofTop = BUILDING_HEIGHT + ROOF_HEIGHT;
    for (const chimney of chimneys(zone, faceOffset * 0.55)) {
      placeParts(
        this.scene,
        CHIMNEY,
        {
          origin: {
            x: chimney.at.x + chimney.offset.x,
            y: roofTop,
            z: chimney.at.z + chimney.offset.z,
          },
          yaw: 0,
        },
        shared,
        `town-chimney-${chimney.col}-${chimney.row}`,
      );
    }

    for (const [i, lamp] of lampPosts(zone, zone.tileSize * 0.33).entries()) {
      const post = this.placeProp('town_lamp_post', { x: lamp.at.x, z: lamp.at.z, yaw: 0 }, LAMP_HEIGHT, `town-lamp-${i}`);
      if (!post) {
        placeParts(this.scene, LAMP_POST, { origin: { x: lamp.at.x, y: 0, z: lamp.at.z }, yaw: 0 }, shared, `town-lamp-${i}`);
        this.lightSpots.push({ x: lamp.at.x, y: LAMP_HEIGHT * 0.9, z: lamp.at.z, colour: TOWN_LAMP_LIGHT });
        continue;
      }
      // The light stays procedural, moved onto the painted lantern (`LAMP_LIGHT_AT`).
      const head = LAMP_POST.filter((part) => part.group === 'glow').map((part) => ({
        ...part,
        size: part.size.map((v) => v * LAMP_LIGHT_AT.scale) as [number, number, number],
        at: [0, LAMP_HEIGHT * LAMP_LIGHT_AT.y, LAMP_HEIGHT * LAMP_LIGHT_AT.z] as [number, number, number],
      }));
      placeParts(this.scene, head, { origin: { x: lamp.at.x, y: 0, z: lamp.at.z }, yaw: 0 }, shared, `town-lamp-${i}`);
      this.lightSpots.push({ x: lamp.at.x, y: LAMP_HEIGHT * LAMP_LIGHT_AT.y, z: lamp.at.z, colour: TOWN_LAMP_LIGHT });
    }

    for (const [i, merlon] of merlons(zone, zone.tileSize / 4).entries()) {
      placeParts(
        this.scene,
        MERLON,
        { origin: { x: merlon.at.x, y: RAMPART_HEIGHT, z: merlon.at.z }, yaw: 0 },
        shared,
        `town-merlon-${i}`,
      );
    }

    this.mergeInto(shared.stone, 'town-stone', palette.stone, root, {
      shadows: true,
      pattern: style.wallPattern,
      salt: 41,
      cell: style.wallCell,
      repeat: wallRepeat(zone, style),
      tint: style.wallTint,
    });
    // **The stalls and the shopkeepers, retextured** (owner's report: *the shop NPCs and kiosks are
    // so basic and ugly, we need to revamp and retexture with what we have*). This is the retexture
    // half — the geometry is unchanged, so a stall is still a counter, two posts, a banner and a
    // sign, and what changes is that they are made of something.
    //
    // The shop's **accent** stays flat and is the reason the rest can be painted: it is the one
    // colour that identifies which artisan this is, it matches the icon in the workshop row, and a
    // painted surface would put wood grain across the one patch whose job is being a colour.
    this.mergeInto(shared.timber, 'town-timber', palette.timber, root, {
      shadows: true,
      // Sawn planks: a counter and a pair of posts are the most obviously *built* things in the
      // game, and plain brown boxes were what made them read as placeholder.
      cell: SCENE.pinePlank,
    });
    // A rim of the figure's own light, exactly as `ModelPalette` gives the player and
    // the monsters. The sun comes from behind the town's north faces, so the only side
    // of a shopkeeper the camera sees is lit by a 0.55 ambient — without this they are
    // a dark smudge against a dark wall, which is what the first capture showed.
    this.mergeInto(shared.cloth, 'town-cloth', palette.cloth, root, {
      shadows: true,
      emissive: scaleColour(palette.cloth, 0.45),
      // Sackcloth, the same cell the robed player wears — a shopkeeper and a mage are dressed from
      // the same bolt, which is what makes a town read as one place.
      cell: GEAR.burlap,
    });
    // Bare skin — the head box and the hands — in flat colour. **The face is not here**: see the
    // `face` group below, and `models/scenery.ts` for why a painting cannot live on a cube.
    this.mergeInto(shared.skin, 'town-skin', palette.skin, root, {
      emissive: scaleColour(palette.skin, 0.42),
    });
    // The face plates, and only the face plates. One material, one cell, one flat surface each.
    this.mergeInto(shared.face, 'town-face', palette.skin, root, {
      emissive: scaleColour(palette.skin, 0.42),
      cell: FACE.skinWeathered,
    });
    // Fully emissive: a window lit only by a 0.55 ambient is a window with the lights
    // off, which is the one thing a window must not look like at night.
    // **The windows, and the whole point of the glow layer.** They were emissive already
    // and read as flat yellow paint, because a self-lit surface with nothing blooming it
    // is a bright square rather than a source. Small and discrete, which is exactly what
    // blooms well — unlike the trim, which is a continuous line.
    this.mergeInto(shared.glow, 'town-glow', palette.glow, root, {
      emissive: palette.glow,
      glow: true,
    });

    for (const [kind, parts] of perShop) {
      const accent = palette.accents[kind];
      this.mergeInto(parts.accent, `town-shop-${kind}`, accent, root, {
        shadows: true,
        // A touch of its own light, so a shop colour still reads on the shaded side.
        emissive: scaleColour(accent, 0.22),
      });
    }
  }

  /**
   * Marks the entry sanctuary and the exit gate on the floor.
   *
   * **The exit is a click target and the entry is not**, which is the difference between
   * the two discs: descending is `CMD_USE_GATE` now rather than something that happened
   * when you stood on it, so the way down needs a collider and a hover outline exactly as
   * a portal and an artisan do. The entry is scenery — nothing is done *to* it — so it
   * stays a mark on the floor.
   *
   * The hub has neither: its way out is a portal, which `buildPortals` registers.
   */
  private buildMarkers(zone: ZonePayload, style: BiomeStyle, root: TransformNode): void {
    const marks: { at: { x: number; z: number }; colour: [number, number, number]; name: string }[] =
      [
        // Cool for safety, the biome's own light for the way out.
        { at: zone.spawn, colour: [0.25, 0.45, 0.7], name: 'zone-entry-mark' },
        { at: zone.exit, colour: style.trim, name: 'zone-exit-mark' },
      ];

    for (const mark of marks) {
      // A sigil underfoot, not a spotlight. Sized in absolute units rather
      // than as a fraction of the tile so it stays a marker whatever the grid
      // scale is.
      const disc = MeshBuilder.CreateCylinder(
        mark.name,
        { height: 0.06, diameter: MARKER_DIAMETER, tessellation: 16 },
        this.scene,
      );
      disc.position.set(mark.at.x, 0.03, mark.at.z);
      disc.parent = root;
      disc.isPickable = false;

      const mat = this.material(mark.name, [
        mark.colour[0] * 0.3,
        mark.colour[1] * 0.3,
        mark.colour[2] * 0.3,
      ]);
      disc.material = mat;

      /*
        Both ways out are click targets, and they are **different kinds** because they do
        different things.

        In a combat zone the sigil is the way *down*: `CMD_USE_GATE`, and the server
        measures the walk. In town the same sigil is the [ GRIND ] gate, which is not a
        gate at all — it opens the level select, a client panel, and `useExit` refuses it
        server-side with `NO_GATE`.

        It was registered in the zone and **not** in town, on the reasoning that a
        collider offering a refused gesture is worse than none. That was half right: the
        gesture was wrong, not the collider. Standing in town and clicking the thing
        labelled THE GRIND did nothing at all, which reads as broken rather than as
        unimplemented.
      */
      if (mark.name === 'zone-exit-mark') {
        const inTown = zone.kind === 'HUB';
        // Phase 42: a stairway around the way down, and an obelisk behind the town's GRIND sigil
        // — on its far rim, away from the camera (which looks from +x, −z), so the walk to the
        // centre never ends inside it. The disc stays, and stays what the collider is measured from.
        const rim = MARKER_DIAMETER / 2 + 0.3;
        const marker = inTown
          ? this.placeProp(
              'marker_grind_entrance_obelisk',
              { x: mark.at.x - rim * Math.SQRT1_2, z: mark.at.z + rim * Math.SQRT1_2, yaw: -Math.PI / 4 },
              OBELISK_HEIGHT,
              'zone-grind-obelisk',
            )
          : this.placeProp('marker_exit_stairway', { x: mark.at.x, z: mark.at.z, yaw: 0 }, MARKER_DIAMETER, 'zone-exit-stairway');
        this.registerObject(
          {
            key: inTown ? 'grind' : 'exit',
            kind: inTown ? 'grind' : 'exit',
            id: inTown ? 'GRIND' : 'EXIT',
            at: { x: mark.at.x, z: mark.at.z },
            // Onto the sigil itself rather than in front of it: `ExitRange` is 3.2 units
            // and the server is what decides the player has arrived, so walking to the
            // centre is the reading that cannot be one step short.
            approach: { x: mark.at.x, z: mark.at.z },
            parts: [disc],
            outline: marker ? [disc, ...marker.meshes] : undefined,
          },
          root,
        );
      }
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Records a clickable world object and builds its invisible collider.
   *
   * **One box, measured from what was built.** The drawn parts stay unpickable, exactly
   * as an entity's animated limbs do and for the same reason: aiming at a merged strip of
   * boxes means aiming at the gaps between them, and the gaps go through to the floor.
   * `measureParts` walks every bounding-box corner, so a rotated artisan gets a box that
   * fits rather than one that assumes the figure faces the camera.
   *
   * The collider is parented to the same node the geometry is, so it goes with the
   * teardown that disposes it and no lifetime has to be remembered twice.
   */
  private registerObject(object: WorldObject, root: TransformNode): void {
    for (const part of object.parts) part.isPickable = false;

    const bounds = measureParts(object.parts, root);
    const collider = MeshBuilder.CreateBox(
      `pick-${object.key}`,
      { width: bounds.width, height: bounds.height, depth: bounds.depth },
      this.scene,
    );
    collider.parent = root;
    collider.position.set(bounds.centreX, bounds.centreY, bounds.centreZ);
    collider.isPickable = true;
    collider.metadata = { worldObject: object.key };
    collider.isVisible = showHitboxes();
    if (collider.isVisible) {
      const wire = new StandardMaterial(`pick-mat-${object.key}`, this.scene);
      wire.wireframe = true;
      wire.emissiveColor = new Color3(0.2, 0.9, 0.4);
      wire.disableLighting = true;
      collider.material = wire;
      this.materials.push(wire);
    }

    this.objects.set(object.key, object);
  }

  private mergeInto(
    meshes: Mesh[],
    name: string,
    colour: [number, number, number],
    root: TransformNode,
    opts: {
      shadows?: boolean;
      emissive?: [number, number, number];
      /** Bloom this surface: it is a light, not merely self-lit. See the note below. */
      glow?: boolean;
      pattern?: Pattern;
      salt?: number;
      /** A painted scene cell, preferred over `pattern`. See `material`. */
      cell?: Cell;
      /** Repeats of that cell per world tile. See `BiomeStyle.surfaceUnits`. */
      repeat?: number;
      /** Map the surface in world space instead, one copy per this many units (`worldUvs`). */
      worldUnits?: number;
      /** Brightness of a painted cell, 1 as painted. See `BiomeStyle.floorTint`. */
      tint?: number;
    } = {},
  ): void {
    this.mergeGroup(meshes, name, colour, root, opts);
  }

  /**
   * `mergeInto`, returning the mesh it produced.
   *
   * The artisans need the handle back — a hover outline is per-mesh, so a figure has to
   * keep hold of the three meshes it merged into. Everything else in the town discards
   * it, which is what `mergeInto` is.
   */
  private mergeGroup(
    meshes: Mesh[],
    name: string,
    colour: [number, number, number],
    root: TransformNode,
    opts: {
      shadows?: boolean;
      emissive?: [number, number, number];
      /** Bloom this surface: it is a light, not merely self-lit. See the note below. */
      glow?: boolean;
      pattern?: Pattern;
      salt?: number;
      /** A painted scene cell, preferred over `pattern`. See `material`. */
      cell?: Cell;
      /** Repeats of that cell per world tile. See `BiomeStyle.surfaceUnits`. */
      repeat?: number;
      /** Map the surface in world space instead, one copy per this many units (`worldUvs`). */
      worldUnits?: number;
      /** Brightness of a painted cell, 1 as painted. See `BiomeStyle.floorTint`. */
      tint?: number;
    } = {},
  ): Mesh | null {
    if (meshes.length === 0) return null;

    const merged = Mesh.MergeMeshes(meshes, true, true, undefined, false, false);
    if (!merged) return null;

    merged.name = name;
    merged.parent = root;
    if (opts.worldUnits) worldUvs(merged, opts.worldUnits);

    const mat = this.material(name, colour, opts.pattern, opts.salt, opts.cell, opts.worldUnits ? 1 : opts.repeat, opts.tint);
    // World UVs run past 0–1 everywhere, so the surface has to wrap: a painted cell already does, a
    // procedural pattern is a clamped DynamicTexture until told.
    if (opts.worldUnits && mat.diffuseTexture) mat.diffuseTexture.wrapU = mat.diffuseTexture.wrapV = Texture.WRAP_ADDRESSMODE;
    if (opts.emissive) mat.emissiveColor = new Color3(...opts.emissive);
    merged.material = mat;

    // **Bloom is opt-in per surface, not "everything emissive".**
    //
    // Doing it by emissive was the obvious version and it was wrong on sight: `zone-trim`
    // is a continuous line along the top of every wall in the zone, so blooming it turned
    // a dark ruin into a neon grid — and the scenery's cloth and skin carry an emissive
    // lift purely to stay readable against a dark floor, so a shopkeeper lit up like a
    // lamp. Emissive answers *is this surface self-lit*; only some of those are meant to
    // read as a **source**.
    if (opts.glow) registerGlow(this.lights.glow, merged, 'world-light');

    // Walls receive shadows but do not cast them: a low sun through a maze of
    // boxes throws long slabs of shadow across the floor and the zone becomes
    // unreadable.
    merged.receiveShadows = opts.shadows ?? false;
    merged.isPickable = false;
    return merged;
  }

  private material(
    name: string,
    colour: [number, number, number],
    pattern?: Pattern,
    salt = 0,
    cell?: Cell,
    repeat = 1,
    tint = 1,
  ): StandardMaterial {
    const mat = new StandardMaterial(name, this.scene);
    mat.diffuseColor = new Color3(...colour);
    mat.specularColor = new Color3(0.02, 0.02, 0.02);
    mat.specularPower = 8;
    // Each of these adds defines to the generated shader, so safe mode strips
    // them to reach the plainest material Babylon will build.
    if (!renderFeatures.specular) mat.disableLighting = true;
    mat.fogEnabled = renderFeatures.fog;

    // A pattern is painted into a DynamicTexture at build time. Skipped in safe
    // mode for the same reason specular is: `?safe=1` exists to reach the
    // simplest material Babylon will compile, and a texture adds a sampler and a
    // define to the shader.
    // **A painted cell wins over the procedural pattern, and the pattern stays as the fallback.**
    //
    // Three ways this ends up on the pattern instead, all of them normal: the biome declares no
    // cell (four of seven do not — see `BiomeStyle.floorCell`), this build has no file for it, or
    // `?textures=0` refused samplers altogether. The order matters because the two are answering
    // the same question and only one can win: a `diffuseTexture` set twice is the second one.
    //
    // `tiling: true`, unlike every character cell. A floor sets one texture repeat per world
    // tile, so a room is the same 128 px cell laid down dozens of times and clamping would
    // stretch one copy across the whole room.
    const painted =
      cell && renderFeatures.textures
        ? cellTexture(this.scene, cell, { tiling: true, repeat })
        : null;
    if (painted) {
      this.textures.push(painted);
      mat.diffuseTexture = painted;
      mat.diffuseColor = new Color3(tint, tint, tint);
    } else if (pattern && renderFeatures.textures) {
      const texture = this.paintTexture(`${name}-tex`, pattern, colour, salt);
      if (texture) {
        mat.diffuseTexture = texture;
        // The colour is already in the texture; leaving diffuseColor at the base
        // would multiply it in twice and darken every surface.
        mat.diffuseColor = new Color3(1, 1, 1);
      }
    }

    this.materials.push(mat);
    return mat;
  }

  /**
   * Paints one pattern into a tiling texture.
   *
   * Returns null when there is no 2D context — jsdom has no canvas, and a headless
   * capture may not either. A missing texture must leave a plain coloured surface
   * rather than a black one, which is why the caller keeps its own colour.
   */
  private paintTexture(
    name: string,
    pattern: Pattern,
    colour: [number, number, number],
    salt: number,
  ): DynamicTexture | null {
    const texture = new DynamicTexture(
      name,
      { width: TEXTURE_SIZE, height: TEXTURE_SIZE },
      this.scene,
      true,
    );
    const ctx = texture.getContext() as CanvasRenderingContext2D | null;
    if (!ctx) {
      texture.dispose();
      return null;
    }

    paint(ctx, pattern, colour, TEXTURE_SIZE, salt);
    texture.update(true);
    // One texture repeat per tile, so the pattern's scale is the same in every
    // zone regardless of how many tiles the floor has.
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
    this.textures.push(texture);
    return texture;
  }

  /** Drops every gate. Its own teardown, because portals outlive no zone. */
  private clearPortals(): void {
    // The gates' entries go with them and the artisans' stay: the two lifetimes share
    // one map, and forgetting that here would leave `objectForMesh` handing out a
    // portal whose meshes have been disposed.
    for (const key of [...this.objects.keys()]) {
      if (key.startsWith('portal:')) this.objects.delete(key);
    }
    // Before the root goes, because the root goes **with its materials** and an arch's is shared.
    for (const arch of this.portalProps) this.props?.release(arch);
    this.portalProps.length = 0;
    this.portalRoot?.dispose(false, true);
    this.portalRoot = null;
    this.portalMaterial?.dispose();
    this.portalMaterial = null;
  }

  private clear(): void {
    // A portal belongs to the zone it was opened in — the server's rule too — so a
    // rebuild takes the gates with it, and the id cache has to go or the next zone's
    // first portal would compare equal to a gate that no longer exists.
    this.clearPortals();
    this.portalIds = '';
    // And the artisans, which belong to the town the way the floor does.
    this.objects.clear();

    for (const floor of this.floors) this.lights.shadows.removeShadowCaster(floor);
    this.floors = [];
    this.lightSpots = [];
    this.fixtureView?.dispose();
    this.fixtureView = null;

    this.root?.dispose(false, true);
    this.root = null;
    for (const prop of this.placed) this.props?.release(prop);
    this.placed.length = 0;
    this.propRoot?.dispose(false, false);
    this.propRoot = null;

    for (const mat of this.materials) mat.dispose();
    this.materials.length = 0;
    // Textures hold GPU memory of their own; a zone change would leak one per
    // material without this.
    for (const tex of this.textures) tex.dispose();
    this.textures.length = 0;
    this.current = null;
  }
}
